import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BarChart3, TrendingUp, Briefcase, Users as UsersIcon, Hourglass, Rocket } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", pm: "PM", designer: "Designer" };
const ROLE_TINT = {
  admin:    "bg-[#E0E7FF] text-[#3730A3] border-[#A5B4FC]",
  pm:       "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]",
  designer: "bg-[#F5F3FF] text-[#5B21B6] border-[#DDD6FE]",
};
const EMPTY_ROLLUP = { projects_count: 0, scope_value: 0, project_cost: 0, margin: 0, revenue: 0 };

export default function AdminOverview() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [staff, setStaff] = useState([]);
  const [totals, setTotals] = useState({
    projects: 0, scope_value: 0, margin: 0, revenue: 0, staff_count: 0,
    active: { projects: 0, scope_value: 0, margin: 0, revenue: 0 },
    prep: { projects: 0, scope_value: 0, margin: 0, revenue: 0 },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/workspace/admin-overview");
        setStaff(data.staff || []);
        setTotals(data.totals || totals);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-16">
        <div className="card-soft text-center py-12" data-testid="admin-overview-forbidden">
          <div className="w-14 h-14 rounded-full bg-[#F7F4EC] flex items-center justify-center mx-auto mb-4">
            <BarChart3 size={22} className="text-[#7A7571]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#2C2A29]">Admin only</h1>
          <p className="text-sm text-[#7A7571] mt-2">
            Ask your firm admin for access.
          </p>
        </div>
      </div>
    );
  }

  const maxRevenue = staff.reduce(
    (m, s) => Math.max(m, (s.active?.revenue || 0) + (s.prep?.revenue || 0)),
    0,
  );

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Workspace</div>
        <h1 className="font-display text-4xl font-bold text-[#2C2A29] mt-2">Admin Overview</h1>
        <p className="text-sm text-[#7A7571] mt-1">
          Workload distribution across your team — projects where each teammate is the{" "}
          <span className="font-semibold text-[#3730A3]">PIC</span>, split by phase
          (<span className="font-semibold text-[#166534]">Active</span> vs{" "}
          <span className="font-semibold text-[#92400E]">Prep&nbsp;Phase</span>).
        </p>
      </div>

      {/* Totals band */}
      <div className="card-soft mb-6" data-testid="ao-totals">
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-6">
          <TotalTile
            icon={<UsersIcon size={16} />} label="Active staff"
            value={totals.staff_count} isMoney={false} testid="ao-total-staff"
          />
          <TotalTile
            icon={<Rocket size={16} className="text-[#166534]" />}
            label="Active projects (PIC)"
            value={totals.active?.projects || 0} isMoney={false}
            testid="ao-total-active-projects"
            note="Only counts projects where the teammate is the Person In Charge — supporting staff aren't double-counted."
          />
          <TotalTile
            icon={<Hourglass size={16} className="text-[#92400E]" />}
            label="Prep Phase (pipeline)"
            value={totals.prep?.projects || 0} isMoney={false}
            testid="ao-total-prep-projects"
            note="Upcoming projects — not yet actively being worked on."
          />
          <TotalTile
            icon={<BarChart3 size={16} />} label="Total scope value"
            value={totals.scope_value} isMoney testid="ao-total-scope"
          />
          <TotalTile
            icon={<TrendingUp size={16} className="text-[#166534]" />} label="Total margin"
            value={totals.margin} isMoney tone={(totals.margin || 0) >= 0 ? "positive" : "negative"}
            testid="ao-total-margin"
            note="Scope value minus internal contractor cost (across active + prep)."
          />
          <TotalTile
            icon={<TrendingUp size={16} />} label="Revenue collected"
            value={totals.revenue} isMoney tone="positive"
            testid="ao-total-revenue"
          />
        </div>
      </div>

      {loading && <p className="text-[#7A7571]">Loading…</p>}
      {!loading && staff.length === 0 && (
        <div className="card-soft text-center py-16 text-[#7A7571]" data-testid="ao-empty">
          No active staff members yet. Invite people from the Team page to see their workload here.
        </div>
      )}

      <div className="space-y-2">
        {staff.map((s) => (
          <StaffRow key={s.user_id} s={s} maxRevenue={maxRevenue} />
        ))}
      </div>
    </div>
  );
}

function StaffRow({ s, maxRevenue }) {
  const initials = (s.name || "?").split(" ").map((c) => c[0]).slice(0, 2).join("").toUpperCase();
  const roleCls = ROLE_TINT[s.role] || "bg-[#F7F4EC] text-[#2C2A29] border-[#EBE5D9]";
  const active = s.active || EMPTY_ROLLUP;
  const prep = s.prep || EMPTY_ROLLUP;
  const combinedRevenue = (active.revenue || 0) + (prep.revenue || 0);
  const revPct = maxRevenue > 0 ? Math.max(4, Math.round((combinedRevenue / maxRevenue) * 100)) : 0;

  return (
    <div className="card-soft !p-4" data-testid={`ao-row-${s.user_id}`}>
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-[#F2CC8F] flex items-center justify-center font-bold text-[#2C2A29] flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#2C2A29] truncate">{s.name}</div>
          <div className="text-sm text-[#7A7571] truncate">{s.email}</div>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${roleCls} flex-shrink-0`}>
          {ROLE_LABEL[s.role] || s.role}
        </span>
      </div>

      {/* Phase-split rollups — Active vs Prep */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <PhaseBlock
          userId={s.user_id}
          phase="active"
          icon={<Rocket size={14} />}
          label="Active"
          tint="bg-[#DCFCE7] text-[#166534] border-[#86EFAC]"
          data={active}
        />
        <PhaseBlock
          userId={s.user_id}
          phase="prep"
          icon={<Hourglass size={14} />}
          label="Prep Phase"
          tint="bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]"
          data={prep}
        />
      </div>

      {/* Revenue bar — combined revenue relative to top earner */}
      {maxRevenue > 0 && (
        <div className="mt-3 h-1.5 rounded-full bg-[#F7F4EC] overflow-hidden" title="Combined revenue relative to top earner">
          <div className="h-full bg-[#166534]" style={{ width: `${revPct}%` }} />
        </div>
      )}
    </div>
  );
}

function PhaseBlock({ userId, phase, icon, label, tint, data }) {
  const margin = Number(data.margin || 0);
  return (
    <div
      className="rounded-2xl border border-[#EBE5D9] bg-[#FDFBF7] p-3"
      data-testid={`ao-phase-${phase}-${userId}`}
    >
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${tint} mb-2`}>
        {icon}{label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <PhaseMetric label="Projects" value={data.projects_count || 0}
          testid={`ao-${phase}-projects-${userId}`} />
        <PhaseMetric label="Scope"
          value={`$${Number(data.scope_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          testid={`ao-${phase}-scope-${userId}`} />
        <PhaseMetric label="Margin"
          value={`${margin >= 0 ? "+" : "−"}$${Math.abs(margin).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          tone={margin >= 0 ? "positive" : "negative"}
          testid={`ao-${phase}-margin-${userId}`} />
        <PhaseMetric label="Revenue"
          value={`$${Number(data.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          tone="positive"
          testid={`ao-${phase}-revenue-${userId}`} />
      </div>
    </div>
  );
}

function PhaseMetric({ label, value, tone, testid }) {
  const cls = tone === "positive"
    ? "text-[#166534]"
    : tone === "negative"
    ? "text-[#9F1239]"
    : "text-[#2C2A29]";
  return (
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#7A7571]">{label}</div>
      <div className={`font-display text-base font-bold mt-0.5 truncate ${cls}`} data-testid={testid} title={typeof value === "string" ? value : undefined}>
        {value}
      </div>
    </div>
  );
}

function TotalTile({ icon, label, value, isMoney, tone, testid, note }) {
  const cls = tone === "positive"
    ? "text-[#166534]"
    : tone === "negative"
    ? "text-[#9F1239]"
    : "text-[#2C2A29]";
  const num = Number(value || 0);
  const formatted = isMoney
    ? `${num < 0 ? "−" : ""}$${Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : num.toLocaleString();
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="text-xs text-[#7A7571] flex items-start gap-1.5 leading-tight min-h-[2.25rem]">
        <span className="flex-shrink-0 mt-0.5">{icon}</span>
        <span className="break-words">{label}</span>
      </div>
      <div className={`font-display text-xl xl:text-2xl font-bold mt-2 ${cls} break-words leading-tight`}>
        {formatted}
      </div>
      {note && <div className="text-[10px] text-[#7A7571] mt-1.5 italic leading-snug break-words">{note}</div>}
    </div>
  );
}
